import store from '@/store/store'
import api from '@/js/services/axios'
import userRepository from '@/js/api/UserRepository'
import commonUtil from '@/js/utils/commonUtil'

/**
 * What `resendVerificationEmail` answers when a limit refuses the send. Passed to the resend
 * cooldown mixin, which is purpose-neutral: password reset is refused by the same limiter under
 * its own codes. Backend: `ApiErrorConstants.EMAIL_VERIFICATION_*`.
 */
export const VERIFICATION_RESEND_LIMIT_CODES = {
  cooldown: 'EMAIL_VERIFICATION_RATE_LIMIT_EXCEEDED',
  dailyLimit: 'EMAIL_VERIFICATION_DAILY_LIMIT_REACHED'
}

export default {
  login (username, password) {
    return new Promise((resolve, reject) => {
      api.post('public/v1/login', { email: username, password: password})
        .then(async response => {
          const data = await this._authroize(response)
          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  resendVerificationEmail (email) {
    return new Promise((resolve, reject) => {
      api.post('public/v1/resend-verification', null, {
        params: { email: email }
      })
        .then(response => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  // Signup new user account (organizationIdentifier optional; when provided used for org URL slug).
  // Name is intentionally not sent as user.name in this flow; signup value maps to organizationName.
  signup (email, name, password, planIdentifier, allowMarketingEmails, approveTermsAndConditions, organizationName, organizationIdentifier, accountType, preferredLanguage) {
    const normalizedOrganizationName = organizationName || name || null
    return new Promise((resolve, reject) => {
      api.post('public/v1/signup', {
        email,
        name: null,
        password,
        planIdentifier,
        allowMarketingEmails,
        approveTermsAndConditions,
        organizationName: normalizedOrganizationName,
        organizationIdentifier: organizationIdentifier || undefined,
        accountType,
        preferredLanguage
      })
        .then(async response => {
          const data = await this._authroize(response)
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  // Verify email using token from email link. On success returns new JWT in header and user in body; updates store with new token.
  verifyEmail (email, token) {
    return new Promise((resolve, reject) => {
      api.post('public/v1/verify-email', null, {
        params: { email, token }
      })
        .then(async (response) => {
          const data = response.data
          const userData = userRepository._processUserResponse(data.user)
          await store.dispatch('user/updateUserDetails', userData)
          resolve(userData)
        })
        .catch(error => reject(error))
    })
  },

  // Request password reset
  requestPasswordReset (email) {
    return new Promise((resolve, reject) => {
      api.post('public/v1/password-reset', null, {
        params: { email: email }
      })
        .then(response => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  // Verify password reset token
  verifyPasswordResetToken (email, token) {
    return new Promise((resolve, reject) => {
      api.get('public/v1/password-reset/verify', {
        params: { email: email, token: token }
      })
        .then(response => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  // Complete password reset
  completePasswordReset (email, token, newPassword) {
    return new Promise((resolve, reject) => {
        api.post('public/v1/password-reset/complete', {
          email: email,
          token: token,
          newPassword: newPassword
        })
        .then(async response => {
          const data = await this._authroize(response)
          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  // Participant signup (unified endpoint for both email/password and Google OAuth)
  participantSignup (email, name, password, organizationId, phone, allowMarketingEmails, approveTermsAndConditions, preferredLanguage, signupType, googleIdToken) {
    return new Promise((resolve, reject) => {
      const payload = {
        email,
        name,
        organizationId,
        // Same normalisation as a guest booking (EventParticipantRepository.addParticipant),
        // so the same number stored from either path looks the same.
        phone: phone ? commonUtil.removeSpaces(phone) : null,
        allowMarketingEmails,
        approveTermsAndConditions,
        preferredLanguage,
        signupType: signupType || 'Email'
      }
      
      // Add password for Password signup, or googleIdToken for Google signup
      if (signupType === 'Google') {
        payload.googleIdToken = googleIdToken
      } else if (signupType === 'Password' || !signupType) {
        // Default to Password if not specified
        payload.password = password
        if (!signupType) {
          payload.signupType = 'Password'
        }
      }
      
      api.post('public/v1/participant/signup', payload)
        .then(async response => {
          const data = await this._authroize(response)
          resolve(data)
        })
        .catch(error => reject(error))
    })
  },

  // Participant signup with Google OAuth (convenience method)
  participantGoogleSignup (email, name, organizationId, phone, allowMarketingEmails, approveTermsAndConditions, preferredLanguage, googleIdToken) {
    return this.participantSignup(
      email,
      name,
      null, // password not needed for Google
      organizationId,
      phone,
      allowMarketingEmails,
      approveTermsAndConditions,
      preferredLanguage,
      'Google',
      googleIdToken
    )
  },

  async _authroize(response) {
    const data = response.data
    const authorization = response.headers["authorization"]
    const userData = userRepository._processUserResponse(data.user)
    data.user = userData
    if (authorization && userData.id) {
      data.token = authorization
      data.user = userData
      await store.dispatch('user/authorize', data)
    }
    return data
  }
}
